import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import React, { useMemo, useState } from 'react';
import {
  User,
  UserRolesEnum,
} from '../../../services/entities/impl/blfApi/schemas/user';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import LoadingSpinner from '../../commonUI/LoadingSpinner';
import { darkenColor } from '../../commonUtils';
import { GetEntityResponse } from './generic/context/CRUDContext';
import { RolesFormState, useRolesFormData } from './hooks/useRolesFormData';

interface Props {
  useGetCurrOnboardUserHook: () => GetEntityResponse<User>;
}

const buttonDefaultStyles: SxProps<Theme> = {
  mb: '1rem',
  bgcolor: (theme) => theme.colors.secondary,
  color: (theme) => theme.colors.common.white,
  '&:hover': {
    bgcolor: (theme) => theme.colors.secondary,
    color: (theme) => theme.colors.common.white,
    opacity: 0.8,
  },
};

export const RolesSubForm: React.FC<Props> = ({
  useGetCurrOnboardUserHook,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    allowedRolesOptions,
    currUser,
    roles,
    customers,
    submitState,
    error,
    addCustomer,
    handleCustomerChange,
    handleRoleChange,
    handleDeleteRole,
    handleSubmitRoles,
  } = useRolesFormData({ useGetCurrOnboardUserHook });
  const [submitBgColor, setSubmitBgColor] = useState<string>(
    theme.colors.secondary
  );

  const customerOptions = useMemo(() => {
    const allCustomersOption = (
      <MenuItem key='*' value='*'>
        All Customers
      </MenuItem>
    );

    if (!customers) return allCustomersOption;

    const options = customers.map((customer) => (
      <MenuItem key={customer.id} value={customer.id}>
        {customer.name}
      </MenuItem>
    ));

    return [allCustomersOption, ...options];
  }, [customers]);

  const roleOptions = useMemo(() => {
    return Object.values(UserRolesEnum)
      .filter((role) => allowedRolesOptions.includes(role))
      .map((roleName) => (
        <MenuItem key={roleName} value={roleName}>
          {roleName}
        </MenuItem>
      ));
  }, [allowedRolesOptions]);

  const submitButtonContent = useMemo(() => {
    if (submitState === RolesFormState.Saving) {
      setSubmitBgColor(theme.colors.secondary);
      return <LoadingSpinner size='20px' color='white' />;
    }
    if (submitState === RolesFormState.Saved) {
      setSubmitBgColor(theme.colors.status.green.main);
      return <DoneIcon />;
    }
    if (submitState === RolesFormState.Error) {
      setSubmitBgColor(theme.colors.status.red.main);
      return <WarningIcon />;
    }
    if (submitState === RolesFormState.Idle) {
      setSubmitBgColor(theme.colors.secondary);
      return t('Submit Roles', { ns: 'common' });
    }
  }, [submitState]);

  if (!currUser) {
    return null;
  }

  return (
    <Box
      component='form'
      sx={{
        width: '100%',
        mt: '1rem',
        p: '2rem',
        bgcolor: theme.colors.darkTheme.grey.dark,
        border: `1px solid ${theme.colors.darkTheme.grey.muted}`,
        borderRadius: theme.borderRadius.primary,
        boxShadow:
          '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'min-content 1fr',
      }}
    >
      <Typography
        sx={{
          color: theme.colors.common.white,
        }}
        variant='h5'
      >
        Permissions
      </Typography>
      <Divider
        sx={{
          width: '100%',
          bgcolor: theme.colors.darkTheme.grey.muted,
          mb: '2rem',
        }}
      />
      {roles.map((role, idx) => (
        <React.Fragment key={idx}>
          <Box
            key={role.selector}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <FormControl sx={{ width: '100%' }} color='secondary'>
              <InputLabel id='customer-label'>
                {t('customers', { ns: 'common' })}
              </InputLabel>
              <Select
                value={role.resourceID || ''}
                label='Customer'
                labelId='customer-label'
                onChange={(e) =>
                  handleCustomerChange(role.selector, e.target.value as string)
                }
                sx={{ width: '100%' }}
                required
              >
                {customerOptions}
              </Select>
            </FormControl>
            <FormControl sx={{ width: '100%' }} color='secondary'>
              <InputLabel id='role-label'>
                {t('roles', { ns: 'common' })}
              </InputLabel>
              <Select
                labelId='role-label'
                value={
                  roles.find((r) => r.selector === role.selector)?.name || ''
                }
                label='Role'
                sx={{ width: '100%' }}
                onChange={(e) =>
                  handleRoleChange(
                    role.selector,
                    e.target.value as UserRolesEnum
                  )
                }
              >
                {roleOptions}
              </Select>
            </FormControl>
            <IconButton
              aria-label='delete'
              onClick={() => handleDeleteRole(role.selector)}
              sx={{ color: theme.colors.common.white }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
          <Divider
            sx={{
              width: '100%',
              bgcolor: theme.colors.common.bloomfieldGreen,
              mb: '1rem',
            }}
          />
        </React.Fragment>
      ))}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <IconButton onClick={addCustomer} sx={buttonDefaultStyles}>
          <AddIcon />
        </IconButton>
      </Box>
      <Button
        onClick={() => handleSubmitRoles()}
        disabled={submitState !== RolesFormState.Idle || !currUser}
        type='submit'
        variant='contained'
        sx={
          {
            width: '100%',
            height: '36.5px',
            bgcolor: submitBgColor,
            '&:hover': {
              bgcolor: darkenColor(submitBgColor, 0.2),
            },
            '&.Mui-disabled': {
              color: (theme) => theme.colors.common.white,
              bgcolor: submitBgColor,
              opacity: 0.6,
            },
          } as SxProps<Theme>
        }
      >
        {submitButtonContent}
      </Button>
      {submitState === RolesFormState.Error && error && (
        <Typography sx={{ color: theme.colors.status.red.main }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};
